import React, { useEffect, useRef } from "react";

/**
 * Grainy pointillist "glitter blob" rendered on a canvas.
 * Mimics the codos.ai warm particle art with subtle twinkle + drift.
 *
 * props:
 *  - size: px canvas square size (default 320)
 *  - blobs: array of {x,y,r} in 0..1 relative coords describing sub-clusters
 *  - density: dots per 100px^2 (default 46)
 *  - palette: array of rgb colors
 *  - className / style
 */
const DEFAULT_PALETTE = [
  "#f2a65a",
  "#f6c177",
  "#ef8f52",
  "#f5a0a8",
  "#f5c451",
  "#e97f5f",
  "#f7d79a",
  "#ef9a6a",
];

export default function ParticleBlob({
  size = 320,
  blobs = [{ x: 0.5, y: 0.5, r: 0.42 }],
  density = 46,
  palette = DEFAULT_PALETTE,
  className = "",
  style = {},
}) {
  const canvasRef = useRef(null);
  const rafRef = useRef(null);
  const dotsRef = useRef([]);
  const runningRef = useRef(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = size * dpr;
    canvas.height = size * dpr;
    const ctx = canvas.getContext("2d");
    ctx.scale(dpr, dpr);

    // build dots — each keeps a home position and a scattered "atom" target
    // inside the canvas, so the cluster can morph apart into a field of bits
    // and atoms and then reassemble.
    const dots = [];
    const half = size / 2;
    const pad = size * 0.05;
    const maxR = half - pad;
    blobs.forEach((b) => {
      const cx = b.x * size;
      const cy = b.y * size;
      const R = b.r * size;
      const area = Math.PI * R * R;
      const count = Math.max(120, Math.round((area / 10000) * density));
      for (let i = 0; i < count; i++) {
        // denser toward center
        const t = Math.pow(Math.random(), 0.6);
        const ang = Math.random() * Math.PI * 2;
        const rr = t * R;
        const hx = cx + Math.cos(ang) * rr;
        const hy = cy + Math.sin(ang) * rr;
        // scattered target: a point in the canvas disk, biased outward from home
        const dirx = hx - cx;
        const diry = hy - cy;
        const baseAng = Math.atan2(diry, dirx) + (Math.random() - 0.5) * 1.8;
        const tr = maxR * Math.sqrt(0.15 + Math.random() * 0.85);
        const sx = half + Math.cos(baseAng) * tr;
        const sy = half + Math.sin(baseAng) * tr;
        dots.push({
          hx,
          hy,
          sx,
          sy,
          // small phase spread so the blob bursts mostly in unison
          burstPhase: (Math.random() - 0.5) * 0.9,
          // orbital jitter (atom-like wobble)
          orad: 1 + Math.random() * 3.5,
          ophase: Math.random() * Math.PI * 2,
          ospd: 0.9 + Math.random() * 2.4,
          s: 1.4 + Math.random() * 3.0,
          c: palette[(Math.random() * palette.length) | 0],
          base: Math.min(1, 0.55 + (1 - t) * 0.45 * Math.random() + 0.28),
          phase: Math.random() * Math.PI * 2,
          spd: 0.6 + Math.random() * 1.6,
          // binary character this particle becomes when it spreads out
          glyph: Math.random() < 0.5 ? "0" : "1",
          glyphPx: 9 + ((Math.random() * 4) | 0),
        });
      }
    });
    dotsRef.current = dots;

    // eased 0..1..0 breathing used to drive the morph
    const wave = (x) => {
      const c = 0.5 - 0.5 * Math.cos(x); // 0..1..0
      return c * c * (3 - 2 * c); // smoothstep for a snappier burst
    };

    // smoothstep helper
    const smooth = (a, b, x) => {
      const t2 = Math.max(0, Math.min(1, (x - a) / (b - a)));
      return t2 * t2 * (3 - 2 * t2);
    };

    let last = 0;
    const render = (ts) => {
      if (!runningRef.current) return;
      // throttle ~30fps
      if (ts - last < 32) {
        rafRef.current = requestAnimationFrame(render);
        return;
      }
      last = ts;
      const time = ts / 1000;
      ctx.clearRect(0, 0, size, size);
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      const arr = dotsRef.current;
      for (let i = 0; i < arr.length; i++) {
        const d = arr[i];
        // dispersal amount (0 = assembled blob, 1 = fully spread into binary)
        const disp = wave(time * 0.62 + d.burstPhase);
        const ojx = Math.cos(time * d.ospd + d.ophase) * d.orad;
        const ojy = Math.sin(time * d.ospd * 1.13 + d.ophase) * d.orad;
        let x = d.hx + (d.sx - d.hx) * disp + ojx * (0.3 + disp);
        let y = d.hy + (d.sy - d.hy) * disp + ojy * (0.3 + disp);
        // the whole scattered field turns as it spreads
        const ang = disp * 0.55 + time * 0.13;
        const ca = Math.cos(ang);
        const sa = Math.sin(ang);
        const dx = x - half;
        const dy = y - half;
        x = half + dx * ca - dy * sa;
        y = half + dx * sa + dy * ca;

        const tw = 0.82 + 0.18 * Math.sin(time * d.spd + d.phase);
        const alpha = Math.min(1, d.base * tw);
        const gm = smooth(0.28, 0.72, disp); // dot -> glyph crossfade

        ctx.fillStyle = d.c;
        if (gm < 0.98) {
          // dot part of the blob
          ctx.globalAlpha = alpha * (1 - gm);
          const s = d.s * (1 - 0.15 * disp);
          ctx.fillRect(x, y, s, s);
        }
        if (gm > 0.02) {
          // binary digit part
          ctx.globalAlpha = alpha * gm;
          ctx.font = `${d.glyphPx}px "IBM Plex Mono", monospace`;
          ctx.fillText(d.glyph, x, y);
        }
      }
      ctx.globalAlpha = 1;
      rafRef.current = requestAnimationFrame(render);
    };

    // draw once statically (assembled state)
    const drawStatic = () => {
      ctx.clearRect(0, 0, size, size);
      const arr = dotsRef.current;
      for (let i = 0; i < arr.length; i++) {
        const d = arr[i];
        ctx.globalAlpha = d.base;
        ctx.fillStyle = d.c;
        ctx.fillRect(d.hx, d.hy, d.s, d.s);
      }
      ctx.globalAlpha = 1;
    };
    drawStatic();

    const io = new IntersectionObserver(
      (entries) => {
        const vis = entries[0].isIntersecting;
        if (vis && !runningRef.current) {
          runningRef.current = true;
          rafRef.current = requestAnimationFrame(render);
        } else if (!vis && runningRef.current) {
          runningRef.current = false;
          cancelAnimationFrame(rafRef.current);
        }
      },
      { threshold: 0.05 }
    );
    io.observe(canvas);

    return () => {
      runningRef.current = false;
      cancelAnimationFrame(rafRef.current);
      io.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [size, density, JSON.stringify(blobs), JSON.stringify(palette)]);

  return (
    <canvas
      ref={canvasRef}
      className={className}
      style={{ width: size, height: size, display: "block", ...style }}
      aria-hidden="true"
    />
  );
}
